import type {
  CreateActionPayload,
  CreateActionResponse,
} from '@/domains/academicRisk/academicRiskAction.entity';
import type { AcademicRiskActionRepository } from '@/domains/academicRisk/academicRiskAction.repository';

/**
 * Use Case para crear y registrar una nueva acción académica en el sistema.
 *
 * Funcionalidad:
 * - Crea una acción académica asociada a un estudiante en riesgo
 * - Registra información sobre intervenciones, reuniones o seguimientos realizados
 * - Persiste la acción en el repositorio mediante la capa de infraestructura
 *
 * Responsabilidades:
 * - Validar estructura del payload antes de enviarlo al repositorio
 * - Coordinar la creación de acciones académicas con el repositorio
 * - Actuar como intermediario entre la capa de dominio y la infraestructura
 * - Propagar errores de persistencia para manejo en capas superiores
 */
export class CreateAcademicRiskActionUseCase {
  private repository: AcademicRiskActionRepository;

  /**
   * Inicializa el UseCase con las dependencias necesarias.
   *
   * @param repository - Repositorio que implementa AcademicRiskActionRepository para operaciones CRUD de acciones
   */
  constructor(repository: AcademicRiskActionRepository) {
    this.repository = repository;
  }

  /**
   * Ejecuta la creación de una nueva acción académica en el sistema.
   *
   * @param payload - Datos de la acción a registrar (tipo, estudiante, riesgo, observaciones, etc.)
   */
  async execute(payload: CreateActionPayload): Promise<CreateActionResponse> {
    return await this.repository.createAction(payload);
  }
}
